/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { stringify as stringifyQuery } from 'query-string';
import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import * as AppPropTypes from '../lib/PropTypes';

import { useApi } from './ApiContext';

const PagesContext = React.createContext(null);

export const usePages = () => useContext(PagesContext);

export const usePage = (url, search = null) => {
    const { pages, setPages } = usePages();
    const api = useApi();
    const key = `${url}${
        search !== null ? stringifyQuery(search, { arrayFormat: 'bracket' }) : ''
    }`;
    const page = pages !== null ? pages[key] || null : null;
    const [loading, setLoading] = useState(page === null);
    const [error, setError] = useState(false);
    const possibleLoading = useMemo(() => page === null, [key, page]);

    useEffect(() => {
        if (page === null) {
            setLoading(true);
            api.pages
                .findByUrl(url, search)
                .then((newPage) => {
                    setPages({
                        ...pages,
                        [key]: newPage,
                    });
                    setLoading(false);
                })
                .catch(() => {
                    setError(true);
                    setLoading(false);
                });
        }
    }, [api, key, url, search, page, pages, setPages, setLoading, setError]);

    return {
        error,
        loading: loading || possibleLoading,
        page,
    };
};

export const useCurrentPage = () => {
    const { pathname = null } = useLocation();
    const { pages } = usePages();
    return pages !== null ? pages[pathname] || null : null;
};

export const useClearPage = () => {
    const { pages, setPages } = usePages();
    return useCallback(
        (url) => {
            setPages(
                Object.keys(pages).reduce(
                    (newPages, key) =>
                        key !== url ? { ...newPages, [key]: pages[key] } : newPages,
                    {},
                ),
            );
        },
        [pages, setPages],
    );
};

const propTypes = {
    pages: PropTypes.objectOf(AppPropTypes.page),
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    pages: null,
};

export const PagesProvider = ({ pages: initialPages, children }) => {
    const [pages, setPages] = useState(initialPages);
    const value = useMemo(
        () => ({
            pages,
            setPages,
        }),
        [pages, setPages],
    );

    return <PagesContext.Provider value={value}>{children}</PagesContext.Provider>;
};

PagesProvider.propTypes = propTypes;
PagesProvider.defaultProps = defaultProps;

export default PagesContext;
